import { SolaView, htmlSolaWithInstance, SolaRouter } from '@hm-spt/sola';

import { AppSidebar } from './components/AppSidebar';

interface AppShellData {
  apiStatus: 'unknown' | 'ok' | 'err';
  apiMessage: string;
}

/**
 * 🎯 AppShell — 어드민 앱의 최상위 레이아웃 컴포넌트
 *
 * 책임:
 *   1. 사이드바(6섹션) + 메인 outlet 영역의 정적 레이아웃 제공
 *   2. SolaRouter 를 outlet 에 attach
 *   3. 라우터 이벤트(route:ready) 구독 → 사이드바 active 갱신
 *
 * 페이지(라우트 뷰)는 outlet 안으로 swap 되고, 사이드바는 한 번만 마운트되어
 * 페이지 전환 때 깜빡임 없음.
 */
export class AppShell extends SolaView<AppShellData> {
  private readonly router: SolaRouter;
  private readonly sidebar = new AppSidebar();

  // 전역 ESC — 열린 모달/팝오버의 딤(overlay) 또는 Close 트리거를 클릭해 닫는다.
  //   (딤 클릭·Close/취소 버튼과 동일한 트리거를 재사용 → 각 뷰 수정 없이 일괄 적용)
  private readonly escHandler = (e: KeyboardEvent): void => {
    // 컴포넌트가 이미 ESC 를 소비했으면(자동완성 listbox·셀 편집 종료 등) 양보.
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    const shown = (el: HTMLElement): boolean => el.getClientRects().length > 0;   // fixed 대응
    const pick = (sel: string): HTMLElement | null => {
      const els = Array.from(document.querySelectorAll<HTMLElement>(sel)).filter(shown);
      return els[els.length - 1] ?? null;   // 문서 순서상 마지막 = 최상단 모달
    };
    // 모달 딤(overlay) 우선, 없으면 팝오버/모달의 Close 트리거.
    const target = pick('[data-action$="-overlay"]') ?? pick('[data-action$="-close"]');
    if (target) { e.preventDefault(); target.click(); }
  };

  constructor(config: { router: SolaRouter }) {
    super();
    this.router = config.router;
    this.data = { apiStatus: 'unknown', apiMessage: 'API 확인 중…' };
  }

  override onMount(): void {
    const outlet = this._rootElement?.querySelector<HTMLElement>('.route-outlet');
    if (!outlet) {
      console.error('[AppShell] .route-outlet element 를 찾을 수 없습니다');
      return;
    }

    // route:ready 이벤트 — 라우트 전환 끝나면 사이드바 active 갱신 + 표시 여부
    this.router.addEventListener('route:ready', () => {
      this.sidebar.setActivePath(location.pathname);
      this.applyNavVisibility();
      this._rootElement?.classList.remove('menu-open');   // 모바일: 메뉴 선택 시 드로어 닫힘
    });

    // 모바일 내비(≤767px): 햄버거 토글 / 딤·닫기 클릭 시 닫힘.
    //   data-action="app-nav-overlay"(끝이 -overlay)라 전역 ESC 핸들러도 닫아준다.
    this._rootElement?.addEventListener('click', (e) => {
      const t = (e.target as HTMLElement).closest('[data-action]');
      const a = t?.getAttribute('data-action');
      if (a === 'app-nav-toggle') this._rootElement!.classList.toggle('menu-open');
      else if (a === 'app-nav-overlay') this._rootElement!.classList.remove('menu-open');
    });

    // 라우터 부착 — 이 시점에 첫 라우트가 즉시 마운트됨
    this.router.attach(outlet);

    // 최초 active 표시 + 사이드바 표시 여부
    this.sidebar.setActivePath(location.pathname);
    this.applyNavVisibility();

    // 전역 ESC 닫기 (앱 전체 모달 공통)
    document.addEventListener('keydown', this.escHandler);
  }

  // 딜 상세(/deal/:id, /deals/new/:id)에서는 좌측 메뉴 숨김 — 넓게 보기.
  //   /deals(목록)은 유지. CSS(.app-shell.is-nav-hidden)가 단일 컬럼으로 전환.
  private applyNavVisibility(): void {
    const p = location.pathname;
    const hideNav = /^\/deal\//.test(p) || /^\/deals\/new\//.test(p);
    this._rootElement?.classList.toggle('is-nav-hidden', hideNav);
  }

  override onUnmount(): void {
    super.onUnmount();
    document.removeEventListener('keydown', this.escHandler);
    this.router.detach();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="app-shell">
        <button class="app-hamburger" type="button" aria-label="메뉴" data-action="app-nav-toggle">☰</button>
        <div class="app-nav-overlay" data-action="app-nav-overlay"></div>
        <button class="app-nav-close" type="button" aria-label="닫기" data-action="app-nav-overlay">✕</button>
        ${this.sidebar}
        <main class="route-outlet"></main>
      </div>
    `;
  }
}
